/**
 * Composant NotificationBell avec support WebSocket temps réel
 * Affiche une cloche avec badge de notifications non lues
 * Se met à jour instantanément via WebSocket
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, CheckCheck, Loader2, Wifi, WifiOff } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import useNotificationWebSocket from '../hooks/useNotificationWebSocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NotificationBell() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  // V36.61 — Filtre par catégorie : 'all' | 'documents' | 'payments' | 'alerts' | 'messages'
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // WebSocket pour notifications temps réel
  const {
    isConnected: wsConnected,
    notifications: _wsNotifications,
    unreadCount: wsUnreadCount,
    lastNotification,
    markAsReadLocal,
    markAllAsReadLocal
  } = useNotificationWebSocket(true);

  // Charger les notifications initiales depuis l'endpoint unifié /notifications/me
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/notifications/me?limit=15`,
        { headers: getAuthHeader() }
      );
      const notifs = response.data.notifications || [];
      const sortedNotifs = notifs.sort((a, b) => 
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setNotifications(sortedNotifs.slice(0, 10));
      setUnreadCount(response.data.unread_count ?? notifs.filter(n => !n.read).length);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [user?.id, getAuthHeader]);

  // Charger au montage et quand l'utilisateur change
  useEffect(() => {
    if (user?.id) {
      fetchNotifications();
    }
  }, [user?.id, fetchNotifications]);

  // Polling de secours (30s) — actif uniquement si WebSocket non connecté
  useEffect(() => {
    if (!user?.id || wsConnected) return;
    const id = setInterval(fetchNotifications, 30000);
    return () => clearInterval(id);
  }, [user?.id, wsConnected, fetchNotifications]);

  // Mettre à jour quand une nouvelle notification WebSocket arrive
  useEffect(() => {
    if (lastNotification && lastNotification.id) {
      setNotifications(prev => {
        // Éviter les doublons
        if (prev.some(n => n.id === lastNotification.id)) {
          return prev;
        }
        return [lastNotification, ...prev].slice(0, 10);
      });
      setUnreadCount(prev => prev + 1);
    }
  }, [lastNotification]);

  // Synchroniser le compteur WebSocket
  useEffect(() => {
    if (wsUnreadCount !== undefined && wsConnected) {
      setUnreadCount(wsUnreadCount);
    }
  }, [wsUnreadCount, wsConnected]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(
        `${API}/notifications/${notificationId}/mark-read`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Mise à jour locale
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      markAsReadLocal(notificationId);
    } catch {
    }
  };

  const markAllRead = async () => {
    try {
      setMarkingAllRead(true);
      await axios.post(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: getAuthHeader() }
      );
      
      // Mise à jour locale
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      markAllAsReadLocal();
    } catch {
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    // V36.69 — Navigation intelligente selon le type de notif + le rôle utilisateur.
    // Priorité 1 : action_url explicite portée par le payload backend.
    const explicitUrl = notif?.data?.action_url || notif?.action_url;
    if (explicitUrl) {
      navigate(explicitUrl);
      return;
    }
    const role = (user?.role || '').toLowerCase();
    const type = (notif?.type || '').toLowerCase();
    const category = getNotificationCategory(notif);

    // Document : tout le monde voit ses propres documents.
    if (notif?.data?.document_id || category === 'documents') {
      if (role === 'super_admin' || role === 'admin') navigate('/admin/super-dashboard?tab=ged-ess');
      else if (role === 'cooper') navigate('/cooper?tab=documents');
      else navigate('/membre?tab=mes-documents');
      return;
    }
    // Paiement / virement / SEPA
    if (category === 'payments' || /virement|sepa|paiement|payment/.test(type)) {
      if (role === 'super_admin' || role === 'admin') navigate('/admin/super-dashboard?tab=alertes-virements');
      else if (role === 'cooper') navigate('/cooper?tab=paiements');
      else navigate('/membre?tab=paiements');
      return;
    }
    // Messages
    if (category === 'messages') {
      if (role === 'super_admin' || role === 'admin') navigate('/admin/super-dashboard?tab=messagerie');
      else if (role === 'cooper') navigate('/cooper?tab=messagerie');
      else navigate('/membre?tab=messagerie');
      return;
    }
    // Alertes : super admin → tab alertes-virements (par défaut le canal le plus utile).
    if (category === 'alerts') {
      if (role === 'super_admin' || role === 'admin') navigate('/admin/super-dashboard?tab=alertes-virements');
      else if (role === 'cooper') navigate('/cooper');
      else navigate('/membre');
      return;
    }
    // Fallback : tableau de bord adapté au rôle.
    if (role === 'super_admin') navigate('/admin/super-dashboard');
    else if (role === 'admin') navigate('/admin/dashboard');
    else if (role === 'cooper') navigate('/cooper');
    else navigate('/membre');
  };

  // V36.69 — Lien « Voir toutes les notifications » : route par rôle pour
  // éviter d'envoyer un super admin sur la page membre.
  const handleSeeAllClick = () => {
    const role = (user?.role || '').toLowerCase();
    if (role === 'super_admin' || role === 'admin') {
      navigate('/admin/super-dashboard?tab=alertes-virements');
    } else if (role === 'cooper') {
      navigate('/cooper?tab=notifications');
    } else {
      navigate('/membre?tab=notifications');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'document': '📄',
      'document_new': '📄✨',
      'ftpe': '🛡️',
      'payment': '💳',
      'alert': '⚠️',
      'success': '✅',
      'info': 'ℹ️',
      'contrat_signe': '📝',
      'new_message': '💬'
    };
    return icons[type] || '🔔';
  };

  /**
   * V36.61 — Catégorise un type de notification dans l'un des 4 buckets
   * du filtre dropdown : documents | payments | alerts | messages.
   * Cas spécial : les notifications avec ``severity=warning|error|critical``
   * sont TOUJOURS rangées dans 'alerts' même si leur type relève d'une
   * autre catégorie (ex: virement_alert avec severity=warning → alerts).
   * Fallback : 'other' (toujours visible quand le filtre = 'all').
   */
  const getNotificationCategory = (notif) => {
    const type = (notif?.type || '').toLowerCase();
    const sev = (notif?.severity || '').toLowerCase();

    // Priorité 1 : severity élevée → bucket Alertes (peu importe le type)
    if (['warning', 'error', 'critical'].includes(sev)) return 'alerts';
    if (/alert|alerte|warning|error|critical|reminder|expir|rejet|incident/.test(type)) return 'alerts';

    // Priorité 2 : messages
    if (/message|chat|mail|email_received|new_email/.test(type)) return 'messages';

    // Priorité 3 : paiements
    if (/payment|virement|paid|acquitte|acompte|stripe|invoice|facture|sepa|bank_transfer|refund|cotisation|cadre_paiement/.test(type)) {
      return 'payments';
    }

    // Priorité 4 : documents
    if (/document|attestation|contrat|conditions_|certificat|signature|cp_generated|pdf|annexe|avenant/.test(type)) {
      return 'documents';
    }

    return 'other';
  };

  // Mémo : compte par catégorie pour les badges du filtre
  const categoryCounts = React.useMemo(() => {
    const acc = { all: 0, documents: 0, payments: 0, alerts: 0, messages: 0 };
    for (const n of notifications) {
      acc.all += 1;
      const c = getNotificationCategory(n);
      if (acc[c] !== undefined) acc[c] += 1;
    }
    return acc;
  }, [notifications]);

  const filteredNotifications = React.useMemo(() => {
    if (categoryFilter === 'all') return notifications;
    return notifications.filter((n) => getNotificationCategory(n) === categoryFilter);
  }, [notifications, categoryFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'À l\'instant';
      if (diffMins < 60) return `Il y a ${diffMins} min`;
      if (diffHours < 24) return `Il y a ${diffHours}h`;
      if (diffDays < 7) return `Il y a ${diffDays}j`;
      
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short'
      });
    } catch {
      return '';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          {/* Indicateur de connexion WebSocket */}
          <span className={`absolute bottom-0 right-0 h-2 w-2 rounded-full ${
            wsConnected ? 'bg-green-500' : 'bg-gray-400'
          }`} title={wsConnected ? 'Temps réel activé' : 'Hors ligne'} />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>Notifications</span>
            {wsConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Wifi className="h-3 w-3" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <WifiOff className="h-3 w-3" />
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* V36.60 — Bouton de purge des doublons (admin seulement) */}
            {(user?.role === 'super_admin' || user?.role === 'admin') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const res = await axios.post(
                      `${API}/virement-alerts/cleanup-duplicates`,
                      {},
                      { headers: getAuthHeader() },
                    );
                    const deleted = res.data?.deleted || 0;
                    if (deleted > 0) {
                      await fetchNotifications();
                    }
                  } catch {
                    /* silent */
                  }
                }}
                className="text-xs h-6 px-1.5 text-violet-600 hover:bg-violet-50"
                title="Purger les notifications doublons (alertes virements répétées)"
                data-testid="notif-cleanup-duplicates-btn"
              >
                Purger
              </Button>
            )}
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={markAllRead}
                disabled={markingAllRead}
                className="text-xs h-6 px-2"
              >
                {markingAllRead ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Tout lire
                  </>
                )}
              </Button>
            )}
          </div>
        </DropdownMenuLabel>
        
        <DropdownMenuSeparator />

        {/* V36.61 — Filtre par catégorie (toujours rendu, même quand vide) */}
        <div className="px-2 pt-2 pb-1 flex items-center gap-1 overflow-x-auto" data-testid="notif-category-filter">
          {[
            { k: 'all',       label: 'Tout',      icon: '🔔' },
            { k: 'documents', label: 'Documents', icon: '📄' },
            { k: 'payments',  label: 'Paiements', icon: '💳' },
            { k: 'alerts',    label: 'Alertes',   icon: '⚠️' },
            { k: 'messages',  label: 'Messages',  icon: '💬' },
          ].map((cat) => {
            const count = categoryCounts[cat.k] || 0;
            const active = categoryFilter === cat.k;
            return (
              <button
                key={cat.k}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCategoryFilter(cat.k); }}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                } ${count === 0 && cat.k !== 'all' ? 'opacity-40' : ''}`}
                data-testid={`notif-filter-${cat.k}`}
                title={`${cat.label} (${count})`}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
                <span className={`text-[9px] tabular-nums ${active ? 'opacity-80' : 'opacity-60'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <DropdownMenuSeparator className="my-0" />
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm" data-testid="notif-empty-state">
            <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            {categoryFilter === 'all'
              ? 'Aucune notification'
              : `Aucune notification dans « ${{
                  documents: 'Documents',
                  payments: 'Paiements',
                  alerts: 'Alertes',
                  messages: 'Messages',
                }[categoryFilter]} »`}
          </div>
        ) : (
          filteredNotifications.map((notif) => (
            <DropdownMenuItem 
              key={notif.id}
              className={`flex items-start gap-3 p-3 cursor-pointer ${
                !notif.read ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
              }`}
              data-testid={`notif-item-${notif.id}`}
              onClick={() => handleNotificationClick(notif)}
            >
              <span className="text-xl flex-shrink-0">
                {getNotificationIcon(notif.type)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`text-sm truncate ${!notif.read ? 'font-semibold' : ''}`}>
                    {notif.titre}
                  </p>
                  {!notif.read && (
                    <span className="h-2 w-2 bg-blue-500 rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                  {notif.message}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDate(notif.created_at || notif.date_creation)}
                </p>
              </div>
            </DropdownMenuItem>
          ))
        )}
        
        {filteredNotifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-center text-sm text-blue-600 hover:text-blue-700 cursor-pointer justify-center"
              data-testid="notif-see-all-link"
              onClick={handleSeeAllClick}
            >
              Voir toutes les notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
